import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';
import NewRequestModal from './NewRequestModal';
import ServiceUpgradeRequired from '../Common/ServiceUpgradeRequired';

// ============================================
// Status Badge Component
// ============================================
const StatusBadge = ({ status }) => {
  const config = {
    sent: { label: 'Sent', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
    in_progress: { label: 'In Progress', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
    signed: { label: 'Signed', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
    completed: { label: 'Completed', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
    declined: { label: 'Declined', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
    expired: { label: 'Expired', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
    unknown: { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  };

  const c = config[status] || config.unknown;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
      {c.label}
    </span>
  );
};

// ============================================
// Stats Card Component
// ============================================
const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
    </div>
  </div>
);

// ============================================
// Empty State Component
// ============================================
const EmptyState = ({ type }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-1">No {type === 'aadhaar' ? 'Aadhaar eSign' : 'Digital Signature'} requests yet</h3>
    <p className="text-sm text-gray-500 max-w-sm">
      {type === 'aadhaar'
        ? 'Send your first document for Aadhaar-based electronic signature using the button above or via the API.'
        : 'Initiate your first digital signature workflow using the button above or via the API.'
      }
    </p>
  </div>
);

// ============================================
// Main ESign Management Component
// ============================================
const ESignManagement = ({ initialTab = 'aadhaar' }) => {
  const { user, logout } = useAuth();

  // Service guard: exempt admin, vendor, accountant; only client role is gated.
  // The eSign menu has two independent sub-services in the admin registry:
  //   - `esign`    → Aadhaar eSign tab
  //   - `digisign` → Digital Signature tab
  // Menu opens if either is enabled; each tab is then gated on its own service.
  const isExempt =
    user?.role === 'admin' ||
    user?.role === 'vendor' ||
    user?.role === 'accountant';
  const isAadhaarEnabled = isExempt || user?.api_services?.esign?.enabled;
  const isDigisignEnabled = isExempt || user?.api_services?.digisign?.enabled;
  const isMenuEnabled = isAadhaarEnabled || isDigisignEnabled;

  const [activeType, setActiveType] = useState(initialTab);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, signed: 0, declined: 0, expired: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const docsPerPage = 20;

  // Sync tab when prop changes (sidebar navigation)
  useEffect(() => {
    if (initialTab) {
      setActiveType(initialTab);
    }
  }, [initialTab]);

  // Fetch documents
  const fetchDocuments = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: docsPerPage.toString(),
        offset: ((page - 1) * docsPerPage).toString(),
        signature_type: activeType,
      });

      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await apiCall(`/api/external/esign/envelopes?${params.toString()}`);
      setDocuments(response.envelopes || []);
      setTotalDocuments(response.total || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching eSign documents:', error);
      showToast.error('Failed to fetch eSign documents');
    } finally {
      setLoading(false);
    }
  }, [activeType, statusFilter, searchQuery]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiCall(`/api/external/esign/stats?signature_type=${activeType}`);
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error fetching eSign stats:', error);
    }
  }, [activeType]);

  // Refetch on filter/type change
  useEffect(() => {
    fetchDocuments(1);
    fetchStats();
  }, [activeType, statusFilter, fetchDocuments, fetchStats]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDocuments(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const totalPages = Math.ceil(totalDocuments / docsPerPage);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    showToast.success('eSign link copied to clipboard');
  };

  const handleDownload = async (envelopeId, documentName) => {
    setDownloadingId(envelopeId);
    try {
      const response = await fetch(`/api/external/esign/download/${envelopeId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentName || envelopeId}_signed.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      showToast.error(error.message || 'Failed to download signed document');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCheckStatus = async (envelopeId) => {
    setCheckingId(envelopeId);
    try {
      const response = await apiCall(`/api/external/esign/check-status/${envelopeId}`, {
        method: 'POST',
      });
      if (response.status_updated) {
        showToast.success(`Status updated to "${response.new_status}"`);
        fetchDocuments(currentPage);
        fetchStats();
      } else {
        showToast.info(`No change — status is still "${response.new_status}"`);
      }
    } catch (error) {
      console.error('Check status error:', error);
      showToast.error('Failed to check status from Meon');
    } finally {
      setCheckingId(null);
    }
  };

  if (!isMenuEnabled) {
    return (
      <ServiceUpgradeRequired
        serviceName="eSign"
        serviceId="esign"
        description="Send documents for Aadhaar-based electronic signatures and digital signature workflows."
      />
    );
  }

  const isActiveTabEnabled = activeType === 'aadhaar' ? isAadhaarEnabled : isDigisignEnabled;
  if (!isActiveTabEnabled) {
    return (
      <ServiceUpgradeRequired
        serviceName={activeType === 'aadhaar' ? 'Aadhaar eSign' : 'Digital Signature'}
        serviceId={activeType === 'aadhaar' ? 'esign' : 'digisign'}
        description={
          activeType === 'aadhaar'
            ? 'Send documents for Aadhaar-based electronic signatures.'
            : 'Send documents for digital signing via Email, URL, or DSC modes.'
        }
      />
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 fade-in">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-4 mb-3">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">eSign Management</h2>
            <p className="text-sm text-gray-500">Track and manage electronic signature requests</p>
          </div>
          <button
            className="px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2"
            onClick={() => setShowNewRequest(true)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Request
          </button>
        </div>

        {/* Type Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => { setActiveType('aadhaar'); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeType === 'aadhaar'
                ? 'bg-white text-emerald-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
              Aadhaar eSign
            </span>
          </button>
          <button
            onClick={() => { setActiveType('digital'); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeType === 'digital'
                ? 'bg-white text-emerald-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Digital Signature
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <StatCard
          label="Total"
          value={stats.total}
          color="bg-blue-50"
          icon={<svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
        />
        <StatCard
          label="Sent"
          value={stats.sent}
          color="bg-yellow-50"
          icon={<svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
        />
        <StatCard
          label="Signed"
          value={stats.signed}
          color="bg-green-50"
          icon={<svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard
          label="Declined"
          value={stats.declined}
          color="bg-red-50"
          icon={<svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <StatCard
          label="Expired"
          value={stats.expired}
          color="bg-gray-50"
          icon={<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-4 py-3 mb-3 flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, document…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="in_progress">In Progress</option>
          <option value="signed">Signed</option>
          <option value="completed">Completed</option>
          <option value="declined">Declined</option>
          <option value="expired">Expired</option>
        </select>

        {/* Refresh */}
        <button
          onClick={() => { fetchDocuments(currentPage); fetchStats(); }}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm flex-1 overflow-hidden flex flex-col min-h-0">
        {loading && documents.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-500">Loading documents…</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <EmptyState type={activeType} />
        ) : (
          <>
            <div className="overflow-x-auto flex-1 min-h-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Document</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Signer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Signed</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map((doc) => (
                    <tr key={doc.envelope_id} className="hover:bg-gray-50 transition-colors">
                      {/* Document Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate max-w-[200px]">{doc.document_name || 'Untitled'}</p>
                            <p className="text-xs text-gray-400 font-mono truncate max-w-[200px]">{doc.envelope_id}</p>
                          </div>
                        </div>
                      </td>

                      {/* Signer */}
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{doc.signer_name || '—'}</p>
                        <p className="text-xs text-gray-400">{doc.signer_email || ''}</p>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={doc.status} />
                      </td>

                      {/* Sent Date */}
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(doc.sent_at)}</td>

                      {/* Signed Date */}
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(doc.signed_at)}</td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {doc.esign_url && doc.status === 'sent' && (
                            <button
                              onClick={() => handleCopyLink(doc.esign_url)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Copy Signing Link"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              </svg>
                            </button>
                          )}
                          {doc.esign_url && doc.status === 'sent' && (
                            <a
                              href={doc.esign_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Open Signing Page"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                          {(doc.status === 'sent' || doc.status === 'in_progress') && (
                            <button
                              onClick={() => handleCheckStatus(doc.envelope_id)}
                              disabled={checkingId === doc.envelope_id}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Check status from Meon"
                            >
                              {checkingId === doc.envelope_id ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              )}
                            </button>
                          )}
                          {(doc.status === 'signed' || doc.status === 'completed') && (
                            <button
                              onClick={() => handleDownload(doc.envelope_id, doc.document_name)}
                              disabled={downloadingId === doc.envelope_id}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Download Signed Document"
                            >
                              {downloadingId === doc.envelope_id ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between bg-gray-50">
                <p className="text-xs text-gray-500">
                  Showing {((currentPage - 1) * docsPerPage) + 1}–{Math.min(currentPage * docsPerPage, totalDocuments)} of {totalDocuments}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchDocuments(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1 text-xs border border-gray-200 rounded-md hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-xs text-gray-700 font-medium">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => fetchDocuments(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1 text-xs border border-gray-200 rounded-md hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* New Request Modal */}
      <NewRequestModal
        isOpen={showNewRequest}
        onClose={() => setShowNewRequest(false)}
        onSuccess={() => {
          setShowNewRequest(false);
          fetchDocuments(1);
          fetchStats();
        }}
      />
    </div>
  );
};

export default ESignManagement;
